import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import { Info, Trash2 } from "lucide-react";
import React from "react";
import {
  formatFollowUpDate,
  getFollowUpTypeLabel,
  safeFormatTimeAgo,
} from "@/features/dashboard/utils/accountsDashboardUtils";

const FollowUpCard = ({ followUp, onAction, onDelete, isDeleting = false }) => {
  const isMeeting = followUp?.item_type === "meeting";
  const isRecommendation = followUp?.item_type === "recommendation";
  const isAiSuggestion = followUp?.item_type === "ai_suggestion";
  const priority = Number(followUp?.priority) || 3;
  const priorityClass =
    priority === 1
      ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
      : priority === 2
        ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
        : "bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300";
  const detail = [followUp?.contact_name, followUp?.channel]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col gap-2.5 p-2.5 rounded-lg bg-white dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/50">
      {/* card header */}
      <div className="flex flex-col gap-0">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-gray-800 dark:text-gray-100 text-[0.75rem] font-medium truncate">
            {followUp?.company_name || "Account follow-up"}
          </h4>
          <div className="flex shrink-0 items-center gap-1">
            <span className="text-[0.6rem] font-medium rounded-full px-1.5 py-0.5 whitespace-nowrap bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary-light">
              {getFollowUpTypeLabel(followUp?.follow_up_type)}
            </span>
            {isAiSuggestion && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(followUp)}
                disabled={isDeleting}
                aria-label="Delete suggestion"
                title="Delete suggestion"
                className="rounded p-0.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900/30 dark:hover:text-red-300"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-1">
          <p className="text-gray-500 dark:text-gray-300 text-[0.69rem] font-normal truncate">
            {followUp?.reason || followUp?.notes || detail || "Follow up with stakeholder"}
          </p>
          {isAiSuggestion && followUp?.notes && (
            <CustomCommonTooltip content={followUp.notes}>
              <Info
                size={12}
                strokeWidth={1.8}
                className="shrink-0 cursor-pointer text-gray-400 dark:text-gray-500"
              />
            </CustomCommonTooltip>
          )}
        </div>
        <p className="text-gray-400 dark:text-gray-400 text-[0.65rem] font-normal">
          {isRecommendation
            ? "Recommended now"
            : isAiSuggestion
              ? `Suggested ${safeFormatTimeAgo(followUp?.scheduled_at)}`
              : formatFollowUpDate(followUp?.scheduled_at)}
          {detail ? ` · ${detail}` : ""}
        </p>
      </div>
      <span className={`self-start text-[0.6rem] font-medium rounded-full px-1.5 py-0.5 ${priorityClass}`}>
        {priority === 1
          ? "High priority"
          : priority === 2
            ? "Priority"
            : isAiSuggestion
              ? "Low priority"
              : "Planned"}
      </span>
      {/* button */}
      <SecondaryButton
        title={followUp?.recommended_action || (isMeeting ? "View Meeting" : "View Follow Up")}
        onClick={() => onAction?.(followUp)}
        disabled={!onAction}
      />
    </div>
  );
};

export default FollowUpCard;
